import { Tank } from "./Tank";
import { HUD_CONFIG, TANK_CONFIG } from "./config";

let crosshair: HTMLImageElement;
let inactivityOverlay: HTMLDivElement | null = null;
let inactivityDetails: HTMLParagraphElement | null = null;

type ScoreboardPlayerStats = {
  playerId: string;
  username: string;
  totalDamageDealt: number;
  kills: number;
  deaths: number;
};

let scoreboardContainer: HTMLDivElement | null = null;
let scoreboardBody: HTMLDivElement | null = null;
let scoreboardPlaceholder: HTMLDivElement | null = null;
let roundTimerDisplay: HTMLSpanElement | null = null;
let scoreboardDataCache: ScoreboardPlayerStats[] = [];
let lastScoreboardHash = "";
let roundEndsAtTimestamp = Date.now() + 10 * 60 * 1000;
let lastTimerLabel = "";
let scoreboardClearedForCurrentRound = false;
let roundTimerIntervalId: number | null = null;
let scoreboardVisible = true;
let scoreboardToggleHandler: ((event: KeyboardEvent) => void) | null = null;
let mobileToggleButton: HTMLButtonElement | null = null;

type HudElementRefs = {
  fpsCounter: HTMLDivElement | null;
  pingCounter: HTMLDivElement | null;
  playersCounter: HTMLDivElement | null;
  botsCounter: HTMLDivElement | null;
  healthBar: HTMLDivElement | null;
  healthText: HTMLDivElement | null;
  reloadProgress: HTMLDivElement | null;
  speedometer: HTMLDivElement | null;
  speedBarFill: HTMLDivElement | null;
  wastedOverlay: HTMLDivElement | null;
  wastedText: HTMLDivElement | null;
  respawnCountdown: HTMLDivElement | null;
};

const hudRefs: HudElementRefs = {
  fpsCounter: null,
  pingCounter: null,
  playersCounter: null,
  botsCounter: null,
  healthBar: null,
  healthText: null,
  reloadProgress: null,
  speedometer: null,
  speedBarFill: null,
  wastedOverlay: null,
  wastedText: null,
  respawnCountdown: null,
};

const hudTextCache = {
  fps: "",
  ping: "",
  players: "",
  bots: "",
  healthText: "",
  speedText: "",
};

const hudStyleCache = {
  pingColor: "",
  healthWidth: "",
  healthColor: "",
  healthAnimation: "",
  healthOpacity: "",
  reloadWidth: "",
  reloadColor: "",
  speedColor: "",
  speedBarWidth: "",
  speedBarColor: "",
};

let lastPlayerStatsUpdateTime = 0;
const PLAYER_STATS_UPDATE_INTERVAL_MS = 500;

// Dodaj style dla czcionki Pricedown
const fontStyle = document.createElement("style");
fontStyle.textContent = `
  @font-face {
    font-family: 'Pricedown';
    src: url('./assets/fonts/pricedownbl.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
  }
`;
document.head.appendChild(fontStyle);

const scoreboardStyle = document.createElement("style");
scoreboardStyle.textContent = `
  #gameScoreboard {
    position: fixed;
    top: 12px;
    right: 12px;
    width: 400px;
    max-width: calc(100vw - 24px);
    background: linear-gradient(140deg, rgba(8, 12, 24, 0.72), rgba(20, 34, 58, 0.78));
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    padding: 20px 20px 18px;
    color: #f4f7ff;
    font-family: "Inter", "Segoe UI", sans-serif;
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(12px);
    z-index: 950;
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  #gameScoreboard.is-hidden {
    opacity: 0;
    transform: translateY(-8px);
    pointer-events: none;
  }

  #gameScoreboard .scoreboard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  #gameScoreboard .scoreboard-title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.65);
  }

  #gameScoreboard .scoreboard-title strong {
    font-size: 18px;
    letter-spacing: 0.08em;
    color: #8cf2ff;
  }

  #gameScoreboard .round-timer {
    font-size: 14px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
    transition: all 0.25s ease;
    min-width: 64px;
    text-align: center;
  }

  #gameScoreboard .round-timer.is-ending {
    color: #ffbe5e;
    box-shadow: 0 0 12px rgba(255, 190, 94, 0.55);
  }

  #gameScoreboard .round-timer.is-finished {
    color: #ff647e;
    border-color: rgba(255, 100, 126, 0.4);
    background: rgba(255, 100, 126, 0.08);
  }

  #gameScoreboard .scoreboard-columns,
  #gameScoreboard .scoreboard-row {
    display: grid;
    grid-template-columns: 60px 1fr 110px 80px;
    column-gap: 8px;
    align-items: center;
  }

  #gameScoreboard .scoreboard-columns {
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.08em;
    color: rgba(255, 255, 255, 0.55);
    margin-bottom: 6px;
  }

  #gameScoreboard .scoreboard-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 500px;
  }

  #gameScoreboard .scoreboard-row {
    padding: 8px 10px;
    border-radius: 12px;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02));
    border: 1px solid rgba(255, 255, 255, 0.05);
    transition: transform 0.2s ease, border 0.2s ease, box-shadow 0.2s ease;
  }

  #gameScoreboard .scoreboard-row:hover {
    transform: translateY(-1px);
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow: 0 10px 18px rgba(0, 0, 0, 0.18);
  }

  #gameScoreboard .scoreboard-row.is-self {
    border-color: rgba(140, 242, 255, 0.5);
    box-shadow: 0 8px 20px rgba(140, 242, 255, 0.12);
    background: linear-gradient(120deg, rgba(140, 242, 255, 0.25), rgba(140, 242, 255, 0.05));
  }

  #gameScoreboard .scoreboard-rank {
    font-family: 'Pricedown', 'Orbitron', sans-serif;
    font-size: 18px;
    color: #8cf2ff;
  }

  #gameScoreboard .scoreboard-row[data-rank="1"] .scoreboard-rank {
    color: #ffd54f;
  }

  #gameScoreboard .scoreboard-row[data-rank="2"] .scoreboard-rank {
    color: #d3e4ff;
  }

  #gameScoreboard .scoreboard-row[data-rank="3"] .scoreboard-rank {
    color: #ffb585;
  }

  #gameScoreboard .scoreboard-player {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  #gameScoreboard .scoreboard-damage,
  #gameScoreboard .scoreboard-kd {
    font-size: 13px;
    font-weight: 600;
    text-align: right;
  }

  #gameScoreboard .scoreboard-damage {
    color: #f7f7ff;
  }

  #gameScoreboard .scoreboard-kd {
    color: #9ddcff;
  }

  #gameScoreboard .scoreboard-placeholder {
    margin-top: 12px;
    padding: 16px;
    border-radius: 12px;
    border: 1px dashed rgba(255, 255, 255, 0.2);
    text-align: center;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
    display: none;
  }

  @media (max-width: 900px) {
    #gameScoreboard {
      width: calc(100% - 32px);
      left: 16px;
      right: 16px;
      top: auto;
      bottom: 16px;
    }
  }

  /* Mobile scoreboard toggle button */
  #scoreboardToggleBtn {
    display: none;
    position: fixed;
    top: 12px;
    right: 12px;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: linear-gradient(140deg, rgba(8, 12, 24, 0.85), rgba(20, 34, 58, 0.9));
    backdrop-filter: blur(12px);
    color: #8cf2ff;
    font-size: 20px;
    cursor: pointer;
    z-index: 960;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s ease, background 0.2s ease;
    align-items: center;
    justify-content: center;
  }

  #scoreboardToggleBtn:active {
    transform: scale(0.95);
  }

  #scoreboardToggleBtn.is-active {
    background: linear-gradient(140deg, rgba(140, 242, 255, 0.3), rgba(20, 34, 58, 0.9));
    border-color: rgba(140, 242, 255, 0.5);
  }

  @media (max-width: 900px), (pointer: coarse) {
    #scoreboardToggleBtn {
      display: flex;
    }
  }
`;
document.head.appendChild(scoreboardStyle);

const SCOREBOARD_MAX_ROWS = 12;
const ROUND_WARNING_THRESHOLD_MS = 60 * 1000;
const ROUND_CRITICAL_THRESHOLD_MS = 15 * 1000;
const TIMER_REFRESH_INTERVAL_MS = 500;
const DEFAULT_PLACEHOLDER_MESSAGE = "Waiting for battlefield intel...";
const SCOREBOARD_USERNAME_MAX_LENGTH = 10;
let scoreboardPlaceholderMessage = DEFAULT_PLACEHOLDER_MESSAGE;

function createScoreboard() {
  destroyScoreboard();

  scoreboardContainer = document.createElement("div");
  scoreboardContainer.id = "gameScoreboard";

  const header = document.createElement("div");
  header.className = "scoreboard-header";

  const titleWrapper = document.createElement("div");
  titleWrapper.className = "scoreboard-title";
  const title = document.createElement("strong");
  title.textContent = "Battle Stats";
  const subtitle = document.createElement("span");
  subtitle.textContent = "Live leaderboard";
  titleWrapper.appendChild(title);
  titleWrapper.appendChild(subtitle);
  header.appendChild(titleWrapper);

  roundTimerDisplay = document.createElement("span");
  roundTimerDisplay.className = "round-timer";
  roundTimerDisplay.textContent = "--:--";
  header.appendChild(roundTimerDisplay);

  scoreboardContainer.appendChild(header);

  const columns = document.createElement("div");
  columns.className = "scoreboard-columns";
  ["Rank", "Player", "Total DMG", "K/D"].forEach((label) => {
    const span = document.createElement("span");
    span.textContent = label;
    columns.appendChild(span);
  });
  scoreboardContainer.appendChild(columns);

  scoreboardBody = document.createElement("div");
  scoreboardBody.className = "scoreboard-body";
  scoreboardContainer.appendChild(scoreboardBody);

  scoreboardPlaceholder = document.createElement("div");
  scoreboardPlaceholder.className = "scoreboard-placeholder";
  scoreboardPlaceholder.textContent = scoreboardPlaceholderMessage;
  scoreboardContainer.appendChild(scoreboardPlaceholder);

  document.body.appendChild(scoreboardContainer);

  // Mobile toggle button
  createMobileToggleButton();

  ensureRoundTimerTicker();
  attachScoreboardToggleHandler();
  updateScoreboardVisibility(true);
  renderScoreboard(true);
  updateRoundTimerDisplay(true);
}

function destroyScoreboard() {
  if (roundTimerIntervalId !== null) {
    window.clearInterval(roundTimerIntervalId);
    roundTimerIntervalId = null;
  }

  if (scoreboardContainer) {
    scoreboardContainer.remove();
  }

  scoreboardContainer = null;
  scoreboardBody = null;
  scoreboardPlaceholder = null;
  roundTimerDisplay = null;
  lastScoreboardHash = "";

  detachScoreboardToggleHandler();
  destroyMobileToggleButton();
}

function ensureScoreboard() {
  if (!scoreboardContainer) {
    createScoreboard();
  }
}

function ensureRoundTimerTicker() {
  if (roundTimerIntervalId !== null) {
    return;
  }
  roundTimerIntervalId = window.setInterval(() => updateRoundTimerDisplay(), TIMER_REFRESH_INTERVAL_MS);
}

function attachScoreboardToggleHandler() {
  if (scoreboardToggleHandler) {
    return;
  }

  scoreboardToggleHandler = (event: KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
      scoreboardVisible = !scoreboardVisible;
      updateScoreboardVisibility();
    }
  };

  document.addEventListener("keydown", scoreboardToggleHandler);
}

function detachScoreboardToggleHandler() {
  if (!scoreboardToggleHandler) {
    return;
  }

  document.removeEventListener("keydown", scoreboardToggleHandler);
  scoreboardToggleHandler = null;
}

// Mobile toggle button for scoreboard
function createMobileToggleButton() {
  if (mobileToggleButton) {
    return;
  }

  mobileToggleButton = document.createElement("button");
  mobileToggleButton.id = "scoreboardToggleBtn";
  mobileToggleButton.innerHTML = "📊"; // Chart icon
  mobileToggleButton.title = "Toggle Scoreboard";
  mobileToggleButton.classList.add("is-active"); // Scoreboard visible by default

  mobileToggleButton.addEventListener("click", () => {
    scoreboardVisible = !scoreboardVisible;
    updateScoreboardVisibility();
    updateMobileToggleButtonState();
  });

  document.body.appendChild(mobileToggleButton);
}

function destroyMobileToggleButton() {
  if (mobileToggleButton) {
    mobileToggleButton.remove();
    mobileToggleButton = null;
  }
}

function updateMobileToggleButtonState() {
  if (!mobileToggleButton) {
    return;
  }
  mobileToggleButton.classList.toggle("is-active", scoreboardVisible);
}

function updateScoreboardVisibility(force = false) {
  ensureScoreboard();
  if (!scoreboardContainer) {
    return;
  }

  if (!force && scoreboardContainer.classList.contains("is-hidden") === !scoreboardVisible) {
    return;
  }

  scoreboardContainer.classList.toggle("is-hidden", !scoreboardVisible);
}

function toggleScoreboardPlaceholder(show: boolean) {
  if (!scoreboardPlaceholder) {
    return;
  }
  scoreboardPlaceholder.textContent = scoreboardPlaceholderMessage;
  scoreboardPlaceholder.style.display = show ? "block" : "none";
}

function renderScoreboard(force = false) {
  ensureScoreboard();
  if (!scoreboardBody) {
    return;
  }

  const entriesToRender = scoreboardDataCache.slice(0, SCOREBOARD_MAX_ROWS);
  const newHash = JSON.stringify(entriesToRender);
  if (!force && newHash === lastScoreboardHash) {
    return;
  }

  lastScoreboardHash = newHash;
  scoreboardBody.textContent = "";

  if (!entriesToRender.length) {
    toggleScoreboardPlaceholder(true);
    return;
  }

  toggleScoreboardPlaceholder(false);
  const fragment = document.createDocumentFragment();

  entriesToRender.forEach((player, index) => {
    const row = document.createElement("div");
    row.className = "scoreboard-row";
    row.dataset.rank = String(index + 1);

    if (window.playerTank?.playerId === player.playerId) {
      row.classList.add("is-self");
    }

    const rankCell = document.createElement("div");
    rankCell.className = "scoreboard-rank";
    rankCell.textContent = `#${index + 1}`;

    const playerCell = document.createElement("div");
    playerCell.className = "scoreboard-player";
    playerCell.textContent = formatPlayerName(player.username);

    const damageCell = document.createElement("div");
    damageCell.className = "scoreboard-damage";
    damageCell.textContent = formatDamage(player.totalDamageDealt);

    const kdCell = document.createElement("div");
    kdCell.className = "scoreboard-kd";
    kdCell.textContent = formatKillDeath(player.kills, player.deaths);

    row.appendChild(rankCell);
    row.appendChild(playerCell);
    row.appendChild(damageCell);
    row.appendChild(kdCell);

    fragment.appendChild(row);
  });

  scoreboardBody.appendChild(fragment);
}

function formatDamage(value: number): string {
  if (!Number.isFinite(value)) {
    return "0";
  }
  return Math.round(value).toLocaleString();
}

function formatKillDeath(kills: number, deaths: number): string {
  const safeKills = Number.isFinite(kills) ? kills : 0;
  const safeDeaths = Number.isFinite(deaths) ? deaths : 0;
  return `${safeKills} / ${safeDeaths}`;
}

function formatPlayerName(name?: string): string {
  const safeName = (name || "Unknown").trim() || "Unknown";
  return safeName.length > SCOREBOARD_USERNAME_MAX_LENGTH
    ? `${safeName.slice(0, SCOREBOARD_USERNAME_MAX_LENGTH)}…`
    : safeName;
}

function formatTimerLabel(ms: number): string {
  const clamped = Math.max(0, ms);
  const minutes = Math.floor(clamped / 60000);
  const seconds = Math.floor((clamped % 60000) / 1000);
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

function updateRoundTimerDisplay(force = false) {
  ensureScoreboard();
  if (!roundTimerDisplay) {
    return;
  }

  const remainingMs = Math.max(0, roundEndsAtTimestamp - Date.now());
  const label = formatTimerLabel(remainingMs);
  if (!force && label === lastTimerLabel) {
    return;
  }

  lastTimerLabel = label;
  roundTimerDisplay.textContent = label;

  roundTimerDisplay.classList.toggle(
    "is-ending",
    remainingMs > ROUND_CRITICAL_THRESHOLD_MS && remainingMs <= ROUND_WARNING_THRESHOLD_MS
  );
  roundTimerDisplay.classList.toggle("is-finished", remainingMs <= ROUND_CRITICAL_THRESHOLD_MS);

  if (remainingMs === 0 && !scoreboardClearedForCurrentRound) {
    scoreboardPlaceholderMessage = "Round complete. Awaiting fresh intel...";
    scoreboardDataCache = [];
    scoreboardClearedForCurrentRound = true;
    lastScoreboardHash = "";
    renderScoreboard(true);
  }
}

export function updateScoreboard(payload: { players: ScoreboardPlayerStats[] }) {
  scoreboardDataCache = Array.isArray(payload?.players) ? payload.players : [];
  scoreboardPlaceholderMessage = DEFAULT_PLACEHOLDER_MESSAGE;
  scoreboardClearedForCurrentRound = false;
  renderScoreboard();
}

export function updateRoundTimer(payload: { roundEndsAt: number; timeLeftMs: number }) {
  const fallbackEndsAt = Date.now() + Math.max(payload?.timeLeftMs || 0, 0);
  roundEndsAtTimestamp = payload?.roundEndsAt || fallbackEndsAt;
  scoreboardClearedForCurrentRound = false;
  updateRoundTimerDisplay(true);
}

export function showInactivityOverlay(data: { message: string; inactiveMinutes: number; timeoutMs: number }) {
  if (!inactivityOverlay || !inactivityDetails) {
    return;
  }

  inactivityDetails.innerText = `${data.message}\n(Bezczynny: ${data.inactiveMinutes} min / Limit: ${Math.round(
    data.timeoutMs / 60000
  )} min)`;
  inactivityOverlay.style.pointerEvents = "all";
  inactivityOverlay.style.opacity = "1";
}

export function createHUD() {
  // Kontener HUD
  const hudContainer = document.createElement("div");
  hudContainer.id = "hudContainer";
  hudContainer.style.position = "absolute";
  hudContainer.style.top = "10px";
  hudContainer.style.left = "10px";
  hudContainer.style.color = HUD_CONFIG.COLORS.LOW_SPEED;
  hudContainer.style.fontFamily = "Arial, sans-serif";
  hudContainer.style.fontSize = "14px";
  hudContainer.style.textShadow = "1px 1px 1px rgba(0,0,0,0.5)";

  // Celownik
  crosshair = document.createElement("img");
  crosshair.src =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="4" stroke="white" stroke-width="2" fill="none"/><line x1="16" y1="6" x2="16" y2="12" stroke="white" stroke-width="2"/><line x1="16" y1="20" x2="16" y2="26" stroke="white" stroke-width="2"/><line x1="6" y1="16" x2="12" y2="16" stroke="white" stroke-width="2"/><line x1="20" y1="16" x2="26" y2="16" stroke="white" stroke-width="2"/></svg>';
  crosshair.style.position = "fixed";
  crosshair.style.top = "50%";
  crosshair.style.left = "50%";
  crosshair.style.transform = "translate(-50%, -50%)";
  crosshair.style.pointerEvents = "none";
  crosshair.style.zIndex = "1000";
  crosshair.style.opacity = "0.8";
  document.body.appendChild(crosshair);

  const fpsCounter = document.createElement("div");
  fpsCounter.id = "fpsCounter";
  hudContainer.appendChild(fpsCounter);
  hudRefs.fpsCounter = fpsCounter;

  // Licznik pingu
  const pingCounter = document.createElement("div");
  pingCounter.id = "pingCounter";
  //pingCounter.style.marginTop = "2px";
  pingCounter.style.fontWeight = "bold";
  pingCounter.textContent = "Ping: -- ms";
  hudContainer.appendChild(pingCounter);
  hudRefs.pingCounter = pingCounter;

  // Statystyki graczy
  const statsContainer = document.createElement("div");
  statsContainer.id = "statsContainer";
  //statsContainer.style.marginTop = "2px";
  statsContainer.style.fontWeight = "bold";
  statsContainer.style.color = "white";
  statsContainer.style.textShadow = "1px 1px 1px rgba(0,0,0,0.5)";

  const playersCounter = document.createElement("div");
  playersCounter.id = "playersCounter";
  //playersCounter.style.marginBottom = "3px";
  playersCounter.textContent = "Players online: 0";
  statsContainer.appendChild(playersCounter);
  hudRefs.playersCounter = playersCounter;

  const botsCounter = document.createElement("div");
  botsCounter.id = "botsCounter";
  botsCounter.textContent = "Bots: 0";
  statsContainer.appendChild(botsCounter);
  hudRefs.botsCounter = botsCounter;

  hudContainer.appendChild(statsContainer);

  // Pasek zdrowia
  const healthBarContainer = document.createElement("div");
  healthBarContainer.id = "healthBarContainer";
  healthBarContainer.style.marginTop = "5px";
  healthBarContainer.style.position = "relative";
  healthBarContainer.style.width = "200px";
  healthBarContainer.style.height = "20px";
  healthBarContainer.style.backgroundColor = "rgba(0,0,0,0.5)";
  healthBarContainer.style.border = "2px solid rgba(255,255,255,0.3)";
  healthBarContainer.style.borderRadius = "4px";

  const healthBar = document.createElement("div");
  healthBar.id = "healthBar";
  healthBar.style.width = "100%";
  healthBar.style.height = "100%";
  healthBar.style.backgroundColor = TANK_CONFIG.HEALTH.HEALTH_BAR_COLORS.HIGH.toString();
  healthBar.style.transition = "width 0.3s ease-out, background-color 0.3s";
  healthBar.style.position = "absolute";
  healthBar.style.borderRadius = "2px";
  hudRefs.healthBar = healthBar;

  const healthText = document.createElement("div");
  healthText.id = "healthText";
  healthText.style.position = "absolute";
  healthText.style.width = "100%";
  healthText.style.textAlign = "center";
  healthText.style.color = "white";
  healthText.style.lineHeight = "20px";
  healthText.style.textShadow = "1px 1px 2px black";
  healthText.style.fontWeight = "bold";
  healthText.style.fontSize = "12px";
  healthText.textContent = "100/100";
  hudRefs.healthText = healthText;

  healthBarContainer.appendChild(healthBar);
  healthBarContainer.appendChild(healthText);
  hudContainer.appendChild(healthBarContainer);

  const reloadContainer = document.createElement("div");
  reloadContainer.style.display = "flex";
  reloadContainer.style.alignItems = "center";
  reloadContainer.style.marginTop = "5px";

  const reloadLabel = document.createElement("span");
  reloadLabel.textContent = "Reload: ";
  reloadLabel.style.color = "white";
  reloadLabel.style.marginRight = "5px";

  const reloadBar = document.createElement("div");
  reloadBar.id = "reloadBar";
  reloadBar.style.width = "100px";
  reloadBar.style.height = "10px";
  reloadBar.style.backgroundColor = "rgba(0,0,0,0.5)";
  reloadBar.style.position = "relative";

  const reloadProgress = document.createElement("div");
  reloadProgress.id = "reloadProgress";
  reloadProgress.style.width = "100%";
  reloadProgress.style.height = "100%";
  reloadProgress.style.backgroundColor = "#00ff00";
  reloadProgress.style.position = "absolute";
  reloadProgress.style.transition = "width 0.1s linear";
  hudRefs.reloadProgress = reloadProgress;

  reloadBar.appendChild(reloadProgress);
  reloadContainer.appendChild(reloadLabel);
  reloadContainer.appendChild(reloadBar);
  hudContainer.appendChild(reloadContainer);

  // Licznik prędkości
  const speedometer = document.createElement("div");
  speedometer.id = "speedometer";
  speedometer.style.marginTop = "10px";
  speedometer.style.fontWeight = "bold";
  speedometer.textContent = "Speed: 0 km/h";
  hudContainer.appendChild(speedometer);
  hudRefs.speedometer = speedometer;

  // Pasek prędkości
  const speedBar = document.createElement("div");
  speedBar.id = "speedBar";
  speedBar.style.width = "100px";
  speedBar.style.height = "5px";
  speedBar.style.backgroundColor = "rgba(0,0,0,0.5)";
  speedBar.style.marginTop = "3px";
  speedBar.style.position = "relative";

  const speedBarFill = document.createElement("div");
  speedBarFill.id = "speedBarFill";
  speedBarFill.style.width = "0%";
  speedBarFill.style.height = "100%";
  speedBarFill.style.backgroundColor = HUD_CONFIG.COLORS.LOW_SPEED;
  speedBarFill.style.position = "absolute";
  speedBarFill.style.transition = "width 0.1s linear, background-color 0.3s";
  hudRefs.speedBarFill = speedBarFill;

  speedBar.appendChild(speedBarFill);
  hudContainer.appendChild(speedBar);

  document.body.appendChild(hudContainer);

  // damage indicator
  const damageIndicator = document.createElement("div");
  damageIndicator.id = "damageIndicator";
  damageIndicator.style.position = "absolute";
  damageIndicator.style.top = "50%";
  damageIndicator.style.left = "50%";
  damageIndicator.style.transform = "translate(-50%, -50%)";
  damageIndicator.style.color = "red";
  damageIndicator.style.fontSize = "48px";
  damageIndicator.style.fontWeight = "bold";
  damageIndicator.style.opacity = "0";
  damageIndicator.style.transition = "opacity 0.5s";
  document.body.appendChild(damageIndicator);

  // Wasted overlay container
  const wastedOverlay = document.createElement("div");
  wastedOverlay.id = "wastedOverlay";
  wastedOverlay.style.position = "fixed";
  wastedOverlay.style.top = "0";
  wastedOverlay.style.left = "0";
  wastedOverlay.style.width = "100vw";
  wastedOverlay.style.height = "100vh";
  wastedOverlay.style.display = "flex";
  wastedOverlay.style.flexDirection = "column";
  wastedOverlay.style.alignItems = "center";
  wastedOverlay.style.justifyContent = "center";
  wastedOverlay.style.background = "rgba(139, 0, 0, 0.4)";
  wastedOverlay.style.opacity = "0";
  wastedOverlay.style.transition = "opacity 0.5s";
  wastedOverlay.style.zIndex = "1000";
  wastedOverlay.style.pointerEvents = "none";

  // Wasted text
  const wastedText = document.createElement("div");
  wastedText.id = "wastedText";
  wastedText.style.color = "#FF0000";
  wastedText.style.fontFamily = "Pricedown, Arial Black, sans-serif";
  wastedText.style.fontSize = "120px";
  wastedText.style.textShadow = "4px 4px 0px #000000";
  wastedText.style.transform = "scale(0)";
  wastedText.style.transition = "transform 0.5s cubic-bezier(0.18, 0.89, 0.32, 1.28)";
  wastedText.textContent = "WASTED";
  wastedOverlay.appendChild(wastedText);
  hudRefs.wastedOverlay = wastedOverlay;
  hudRefs.wastedText = wastedText;

  // Respawn countdown
  const respawnCountdown = document.createElement("div");
  respawnCountdown.id = "respawnCountdown";
  respawnCountdown.style.color = "#FFFFFF";
  respawnCountdown.style.fontFamily = "Arial, sans-serif";
  respawnCountdown.style.fontSize = "32px";
  respawnCountdown.style.marginTop = "30px";
  respawnCountdown.style.textShadow = "2px 2px 0px #000000";
  respawnCountdown.style.opacity = "0";
  respawnCountdown.style.transition = "opacity 0.3s";
  respawnCountdown.textContent = "Respawn za 5...";
  wastedOverlay.appendChild(respawnCountdown);
  hudRefs.respawnCountdown = respawnCountdown;

  document.body.appendChild(wastedOverlay);

  // Inactivity overlay (hidden by default)
  inactivityOverlay = document.createElement("div");
  inactivityOverlay.id = "inactiveOverlay";
  inactivityOverlay.style.position = "fixed";
  inactivityOverlay.style.top = "0";
  inactivityOverlay.style.left = "0";
  inactivityOverlay.style.width = "100vw";
  inactivityOverlay.style.height = "100vh";
  inactivityOverlay.style.display = "flex";
  inactivityOverlay.style.flexDirection = "column";
  inactivityOverlay.style.alignItems = "center";
  inactivityOverlay.style.justifyContent = "center";
  inactivityOverlay.style.background = "rgba(0, 0, 0, 0.85)";
  inactivityOverlay.style.color = "#ffffff";
  inactivityOverlay.style.fontFamily = "Pricedown, Arial Black, sans-serif";
  inactivityOverlay.style.textAlign = "center";
  inactivityOverlay.style.fontSize = "64px";
  inactivityOverlay.style.textShadow = "2px 2px 0 #000";
  inactivityOverlay.style.zIndex = "1500";
  inactivityOverlay.style.opacity = "0";
  inactivityOverlay.style.pointerEvents = "none";
  inactivityOverlay.style.transition = "opacity 0.3s ease";

  const inactivityTitle = document.createElement("div");
  inactivityTitle.textContent = "Zbyt długa bezczynność";
  inactivityOverlay.appendChild(inactivityTitle);

  inactivityDetails = document.createElement("p");
  inactivityDetails.style.marginTop = "16px";
  inactivityDetails.style.fontSize = "24px";
  inactivityDetails.style.fontFamily = "Arial, sans-serif";
  inactivityDetails.style.textShadow = "none";
  inactivityOverlay.appendChild(inactivityDetails);

  const inactivityHint = document.createElement("p");
  inactivityHint.style.marginTop = "12px";
  inactivityHint.style.fontSize = "18px";
  inactivityHint.style.fontFamily = "Arial, sans-serif";
  inactivityHint.style.textShadow = "none";
  inactivityHint.textContent = "Odśwież stronę żeby dołączyć ponownie.";
  inactivityOverlay.appendChild(inactivityHint);

  document.body.appendChild(inactivityOverlay);

  createScoreboard();
}

export function removeHUD() {
  const hudContainer = document.getElementById("hudContainer");
  if (hudContainer) {
    hudContainer.remove();
  }

  hudRefs.fpsCounter = null;
  hudRefs.pingCounter = null;
  hudRefs.playersCounter = null;
  hudRefs.botsCounter = null;
  hudRefs.healthBar = null;
  hudRefs.healthText = null;
  hudRefs.reloadProgress = null;
  hudRefs.speedometer = null;
  hudRefs.speedBarFill = null;
  hudRefs.wastedOverlay = null;
  hudRefs.wastedText = null;
  hudRefs.respawnCountdown = null;

  hudTextCache.fps = "";
  hudTextCache.ping = "";
  hudTextCache.players = "";
  hudTextCache.bots = "";
  hudTextCache.healthText = "";
  hudTextCache.speedText = "";

  hudStyleCache.pingColor = "";
  hudStyleCache.healthWidth = "";
  hudStyleCache.healthColor = "";
  hudStyleCache.healthAnimation = "";
  hudStyleCache.healthOpacity = "";
  hudStyleCache.reloadWidth = "";
  hudStyleCache.reloadColor = "";
  hudStyleCache.speedColor = "";
  hudStyleCache.speedBarWidth = "";
  hudStyleCache.speedBarColor = "";

  lastPlayerStatsUpdateTime = 0;
}

export function updateHUD(tank: Tank, fps: number, deltaTime: number) {
  const now = Date.now();
  const fpsCounter = hudRefs.fpsCounter;
  if (fpsCounter) {
    const nextFpsText = `FPS: ${fps}`;
    if (nextFpsText !== hudTextCache.fps) {
      fpsCounter.textContent = nextFpsText;
      hudTextCache.fps = nextFpsText;
    }
  }

  // Update player and bot stats less frequently than frame rate.
  if (now - lastPlayerStatsUpdateTime >= PLAYER_STATS_UPDATE_INTERVAL_MS) {
    updatePlayerStats();
    lastPlayerStatsUpdateTime = now;
  }

  // Aktualizacja licznika pingu
  const pingCounter = hudRefs.pingCounter;
  if (pingCounter) {
    const pingValue = typeof window.currentPing === "number" ? window.currentPing : null;
    const pingText = pingValue === null ? "Ping: -- ms" : `Ping: ${pingValue} ms`;
    if (pingText !== hudTextCache.ping) {
      pingCounter.textContent = pingText;
      hudTextCache.ping = pingText;
    }

    // Zmiana koloru w zależności od wartości pingu
    let pingColor = pingValue === null ? "#aaaaaa" : HUD_CONFIG.COLORS.PING_GOOD;
    if (pingValue !== null && pingValue > HUD_CONFIG.PING_THRESHOLDS.HIGH) {
      pingColor = HUD_CONFIG.COLORS.PING_VERY_HIGH;
    } else if (pingValue !== null && pingValue > HUD_CONFIG.PING_THRESHOLDS.MEDIUM) {
      pingColor = HUD_CONFIG.COLORS.PING_HIGH;
    } else if (pingValue !== null && pingValue > HUD_CONFIG.PING_THRESHOLDS.GOOD) {
      pingColor = HUD_CONFIG.COLORS.PING_MEDIUM;
    }
    if (pingColor !== hudStyleCache.pingColor) {
      pingCounter.style.color = pingColor;
      hudStyleCache.pingColor = pingColor;
    }
  }

  // Damage indicator is now handled by the showDamageIndicator function
  // and called directly when damage is received

  // Aktualizacja paska zdrowia
  const healthBar = hudRefs.healthBar;
  const healthText = hudRefs.healthText;
  if (healthBar && healthText && tank) {
    const healthPercent = Math.max(0, Math.min(100, (tank.health / tank.maxHealth) * 100));
    const healthWidth = `${healthPercent}%`;
    if (healthWidth !== hudStyleCache.healthWidth) {
      healthBar.style.width = healthWidth;
      hudStyleCache.healthWidth = healthWidth;
    }

    const healthLabel = `${Math.ceil(tank.health)}/${tank.maxHealth}`;
    if (healthLabel !== hudTextCache.healthText) {
      healthText.textContent = healthLabel;
      hudTextCache.healthText = healthLabel;
    }

    // Zmiana koloru w zależności od poziomu zdrowia
    let color = TANK_CONFIG.HEALTH.HEALTH_BAR_COLORS.HIGH;
    if (healthPercent <= 30) {
      color = TANK_CONFIG.HEALTH.HEALTH_BAR_COLORS.LOW;
    } else if (healthPercent <= 60) {
      color = TANK_CONFIG.HEALTH.HEALTH_BAR_COLORS.MEDIUM;
    }
    const healthColor = `#${color.toString(16).padStart(6, "0")}`;
    if (healthColor !== hudStyleCache.healthColor) {
      healthBar.style.backgroundColor = healthColor;
      hudStyleCache.healthColor = healthColor;
    }

    // Dodaj efekt migotania gdy czołg jest nietykalny
    const invulnerable = now < tank.invulnerableUntil;
    const animation = invulnerable ? "invulnerable-flash 0.5s infinite" : "";
    const opacity = invulnerable ? "0.8" : "1";
    if (animation !== hudStyleCache.healthAnimation) {
      healthBar.style.animation = animation;
      hudStyleCache.healthAnimation = animation;
    }
    if (opacity !== hudStyleCache.healthOpacity) {
      healthBar.style.opacity = opacity;
      hudStyleCache.healthOpacity = opacity;
    }
  }

  const reloadProgress = hudRefs.reloadProgress;
  if (reloadProgress && tank) {
    const progress = Math.min(1, (now - tank.lastShotTime) / tank.shotCooldown);
    const reloadWidth = `${progress * 100}%`;
    if (reloadWidth !== hudStyleCache.reloadWidth) {
      reloadProgress.style.width = reloadWidth;
      hudStyleCache.reloadWidth = reloadWidth;
    }
    const reloadColor = progress >= 1 ? "#00ff00" : "#ff9900";
    if (reloadColor !== hudStyleCache.reloadColor) {
      reloadProgress.style.backgroundColor = reloadColor;
      hudStyleCache.reloadColor = reloadColor;
    }
  }

  const speedometer = hudRefs.speedometer;
  const speedBar = hudRefs.speedBarFill;
  if (speedometer && tank) {
    const currentSpeed = tank.getCurrentSpeed(deltaTime);
    const absCurrentSpeed = Math.abs(currentSpeed);
    const speedKmh = Math.round(tank.getSpeedKmh());

    // Dodaj informację o kierunku jazdy
    const direction = currentSpeed < 0 ? "↓" : currentSpeed > 0 ? "↑" : "";
    const boostIndicator = tank.getBoostStatus() ? "🚀" : "";

    // Dodaj informację o rzeczywistej prędkości w jednostkach gry (dla celów debugowania)
    const realSpeedInfo = `(${absCurrentSpeed.toFixed(2)})`;

    const speedText = `Speed: ${direction} ${speedKmh} km/h ${boostIndicator} ${realSpeedInfo}`;
    if (speedText !== hudTextCache.speedText) {
      speedometer.textContent = speedText;
      hudTextCache.speedText = speedText;
    }

    // Zmiana koloru w zależności od prędkości
    let speedColor = HUD_CONFIG.COLORS.LOW_SPEED;
    if (speedKmh > HUD_CONFIG.SPEED_THRESHOLDS.HIGH) {
      speedColor = HUD_CONFIG.COLORS.VERY_HIGH_SPEED;
    } else if (speedKmh > HUD_CONFIG.SPEED_THRESHOLDS.MEDIUM) {
      speedColor = HUD_CONFIG.COLORS.HIGH_SPEED;
    } else if (speedKmh > HUD_CONFIG.SPEED_THRESHOLDS.LOW) {
      speedColor = HUD_CONFIG.COLORS.MEDIUM_SPEED;
    }
    if (speedColor !== hudStyleCache.speedColor) {
      speedometer.style.color = speedColor;
      hudStyleCache.speedColor = speedColor;
    }

    // Aktualizacja paska prędkości
    if (speedBar) {
      const maxSpeed = Math.max(0.0001, tank.movementSystem.getMaxSpeed());
      const speedPercent = Math.min(100, (absCurrentSpeed / maxSpeed) * 100);
      const speedWidth = `${speedPercent}%`;
      if (speedWidth !== hudStyleCache.speedBarWidth) {
        speedBar.style.width = speedWidth;
        hudStyleCache.speedBarWidth = speedWidth;
      }

      // Zmiana koloru paska w zależności od prędkości
      let speedBarColor = HUD_CONFIG.COLORS.LOW_SPEED;
      if (speedKmh > HUD_CONFIG.SPEED_THRESHOLDS.HIGH) {
        speedBarColor = HUD_CONFIG.COLORS.VERY_HIGH_SPEED;
      } else if (speedKmh > HUD_CONFIG.SPEED_THRESHOLDS.MEDIUM) {
        speedBarColor = HUD_CONFIG.COLORS.HIGH_SPEED;
      } else if (speedKmh > HUD_CONFIG.SPEED_THRESHOLDS.LOW) {
        speedBarColor = HUD_CONFIG.COLORS.MEDIUM_SPEED;
      }
      if (speedBarColor !== hudStyleCache.speedBarColor) {
        speedBar.style.backgroundColor = speedBarColor;
        hudStyleCache.speedBarColor = speedBarColor;
      }
    }
  }
}

// Dodaj style CSS dla efektu migotania
const style = document.createElement("style");
style.textContent = `
  @keyframes invulnerable-flash {
    0% { opacity: 0.8; }
    50% { opacity: 0.4; }
    100% { opacity: 0.8; }
  }
`;
document.head.appendChild(style);

export function getCrosshair(): HTMLImageElement {
  return crosshair;
}

export function setCrosshairVisibility(visible: boolean) {
  if (crosshair) {
    crosshair.style.display = visible ? "block" : "none";
  }
}

// Zmienna do przechowywania interwału odliczania
let respawnCountdownInterval: ReturnType<typeof setInterval> | null = null;

// Dodaj funkcję do pokazywania napisu Wasted z odliczaniem
export function showWastedOverlay(respawnTimeSeconds: number = 5) {
  const wastedOverlay = hudRefs.wastedOverlay;
  const wastedText = hudRefs.wastedText;
  const respawnCountdown = hudRefs.respawnCountdown;

  if (!wastedOverlay || !wastedText || !respawnCountdown) return;

  // Wyczyść poprzedni interwał jeśli istnieje
  if (respawnCountdownInterval) {
    clearInterval(respawnCountdownInterval);
    respawnCountdownInterval = null;
  }

  // Pokaż overlay
  wastedOverlay.style.opacity = "1";

  // Animacja tekstu WASTED
  setTimeout(() => {
    wastedText.style.transform = "scale(1)";
  }, 100);

  // Pokaż odliczanie po chwili
  setTimeout(() => {
    respawnCountdown.style.opacity = "1";
  }, 600);

  // Rozpocznij odliczanie
  let timeLeft = respawnTimeSeconds;
  respawnCountdown.textContent = `Respawn za ${timeLeft}...`;

  respawnCountdownInterval = setInterval(() => {
    timeLeft--;
    if (timeLeft > 0) {
      respawnCountdown.textContent = `Respawn za ${timeLeft}...`;
    } else {
      respawnCountdown.textContent = "Respawnowanie...";
    }
  }, 1000);

  // Schowaj overlay po czasie respawnu
  setTimeout(() => {
    hideWastedOverlay();
  }, respawnTimeSeconds * 1000);
}

// Funkcja do ukrywania overlay (wywoływana też przy respawnie)
export function hideWastedOverlay() {
  const wastedOverlay = hudRefs.wastedOverlay;
  const wastedText = hudRefs.wastedText;
  const respawnCountdown = hudRefs.respawnCountdown;

  // Wyczyść interwał
  if (respawnCountdownInterval) {
    clearInterval(respawnCountdownInterval);
    respawnCountdownInterval = null;
  }

  if (wastedOverlay) {
    wastedOverlay.style.opacity = "0";
  }
  if (wastedText) {
    wastedText.style.transform = "scale(0)";
  }
  if (respawnCountdown) {
    respawnCountdown.style.opacity = "0";
  }
}

export function updatePlayerStats() {
  const playersCounter = hudRefs.playersCounter;
  const botsCounter = hudRefs.botsCounter;

  if (playersCounter && window.gameState) {
    const players = window.gameState.tanks?.filter((tank) => !tank.playerId.startsWith("bot-")).length || 0;
    const playersText = `Players online: ${players}`;
    if (playersText !== hudTextCache.players) {
      playersCounter.textContent = playersText;
      hudTextCache.players = playersText;
    }
  }

  if (botsCounter && window.gameState?.botManager) {
    const botsCount = window.gameState.botManager.getBotCount();
    const botsText = `Bots: ${botsCount}`;
    if (botsText !== hudTextCache.bots) {
      botsCounter.textContent = botsText;
      hudTextCache.bots = botsText;
    }
  }
}
